/**
 * Pure routing logic for the RMS interaction notes toggle (#10280).
 *
 * The three-button notes toggle ("My Notes" / "AI Notes" / "View All") renders
 * collaborative editors bound to a specific interaction + content kind. For a
 * *group note*, "My Notes" must bind to the viewer's OWN personal interaction
 * (private), while the shared team doc moves under a renamed "Group Note"
 * button. For every other interaction the toggle is unchanged.
 *
 * These helpers are the routing decision for #10280, and `InteractionPage.tsx`
 * calls them directly at each render site so the tests exercise the shipped
 * path (not a parallel copy). Editor selectors are generic over the editor
 * type and tested with sentinels, mirroring `interactionNotesEditorSelection`.
 */

export type NoteContentMode = "notes" | "enhanced" | "all";

/**
 * What the shared team document is called, wherever it is named (#10280).
 * Read by the stacked view's toggle button and by the banner over the manual
 * half of that view, which are separate claims that must not drift apart.
 */
export const GROUP_NOTE_LABEL = "Group Note";

export interface NoteToggleLabels {
  notes: string;
  enhanced: string;
  all: string;
}

export function noteToggleLabels(isGroupNote: boolean): NoteToggleLabels {
  return {
    notes: "My Notes",
    enhanced: "AI Notes",
    // On a group note the stacked view is the shared team doc, so it reads as
    // "Group Note"; elsewhere it's the generic "View All" of the one note.
    all: isGroupNote ? GROUP_NOTE_LABEL : "View All",
  };
}

/**
 * Labels for the file-viewer note surface's toggle (#11763 reopen).
 *
 * That surface has no personal editor: its "notes" half IS the shared
 * document, so on a group note the first button is named for what it opens,
 * and the stacked view stays a plain "View All" of the one note. Deliberately
 * a separate helper from `noteToggleLabels`, which describes the interaction
 * page, where "notes" routes to a private editor and the stacked view is
 * where the shared document lives. A single helper with a flag would hide
 * that the two surfaces genuinely differ.
 */
export function viewerNoteToggleLabels(isGroupNote: boolean): NoteToggleLabels {
  return {
    notes: isGroupNote ? GROUP_NOTE_LABEL : "My Notes",
    enhanced: "AI Notes",
    all: "View All",
  };
}

export function shouldShowNoteToggle(input: {
  isGroupNote: boolean;
  hasEnhancedNotes: boolean;
}): boolean {
  // A group note always needs the toggle so the viewer can reach My Notes vs
  // Group Note. Other interactions only show it once AI notes exist (the
  // pre-existing behavior — otherwise there's nothing to toggle to).
  return input.isGroupNote || input.hasEnhancedNotes;
}

/**
 * Which collaborative editor renders in the SINGLE view of a group note. "My
 * Notes" ("notes") binds to the viewer's personal editor — never the shared
 * group doc, which was the leak in #10280; "AI Notes" ("enhanced") binds to the
 * group-level AI editor. The stacked "all" ("Group Note") view is rendered
 * separately from the group editors.
 */
export function selectGroupSingleEditor<E>(args: {
  mode: NoteContentMode;
  personalNotesEditor: E | null;
  enhancedEditor: E | null;
}): E | null {
  return args.mode === "enhanced"
    ? args.enhancedEditor
    : args.personalNotesEditor;
}

/**
 * The interaction the SINGLE view of a group note is bound to — the mirror of
 * `selectGroupSingleEditor`, and it must move with it. The two editors belong
 * to *different* interactions ("My Notes" is the viewer's private copy), and an
 * inline image is authorized against, and linked to, the interaction it was
 * uploaded to. Pairing an editor with the other interaction's id 404s every
 * hosted image in it (#10058).
 */
export function selectGroupSingleInteractionId(args: {
  mode: NoteContentMode;
  personalInteractionId: string | null;
  groupInteractionId: string | null;
}): string | null {
  return args.mode === "enhanced"
    ? args.groupInteractionId
    : args.personalInteractionId;
}

/**
 * The manual-notes editor the toolbar should target. On a group note, "My
 * Notes" edits the viewer's personal editor; the stacked "Group Note" view and
 * every non-group interaction edit the shared/single manual editor. Feeds
 * `selectActiveEditor` as its `manualEditor`.
 */
export function selectToolbarManualEditor<E>(args: {
  isGroupNote: boolean;
  mode: NoteContentMode;
  personalNotesEditor: E | null;
  groupManualEditor: E | null;
}): E | null {
  return args.isGroupNote && args.mode === "notes"
    ? args.personalNotesEditor
    : args.groupManualEditor;
}

/**
 * Read-only HTML for the view-only (share-recipient) single view. A group
 * note's "My Notes" must never show the shared group doc to a viewer — their
 * personal notes aren't surfaced here, so it renders empty (#10280).
 */
export function selectViewerSingleHtml(args: {
  isGroupNote: boolean;
  mode: NoteContentMode;
  notes: string;
  enhancedNotes: string;
}): string {
  if (args.mode === "enhanced") return args.enhancedNotes;
  if (args.isGroupNote) return "";
  return args.notes;
}

/**
 * The banner over the manual half of a stacked notes view, or null when that
 * half needs no banner (#11763). Read by the interaction page's combined
 * ("Group Note") view and by the file-viewer note surface's "View All".
 *
 * A stacked view banners only its lower half ("AI Notes"), so on a group note
 * the unlabelled upper half — the shared team document — was read under the
 * toggle's "My Notes". Only the shared document is named: an unlabelled
 * personal note reads as the viewer's own, which it is.
 */
export function combinedManualSectionLabel(
  isGroupNote: boolean
): string | null {
  return isGroupNote ? GROUP_NOTE_LABEL : null;
}
